import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { CourseStep, FormFieldSource, PaginationResponse } from 'types';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';
import { coursesApi, coursesTagType } from '../courses';

import {
  ListRequest,
  ListResponse,
  CreateRequest,
  UpdateRequest,
  DetailsRequest,
  DuplicateRequest,
  CourseStepResponse,
  FieldSourcesRequest,
  FormFieldSourcesResponse,
} from './types';

const tagType = 'COURSE_STEPS';
const fieldSourcesTagType = 'COURSE_STEP_FIELD_SOURCES';
const fieldSourcesListTag = { id: 'LIST', type: fieldSourcesTagType } as const;
const getCourseStepTag = (id: string) => ({ id, type: tagType }) as const;
const getFieldSourcesByCourseTag = (courseId: string) =>
  ({ id: courseId, type: fieldSourcesTagType }) as const;

export const courseStepsApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'courseStepsApi',
  tagTypes: [tagType, fieldSourcesTagType],
  endpoints: (build) => ({
    create: build.mutation<CourseStep, CreateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: CourseStepResponse) => response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.COURSE_STEPS_LIST,
      }),
    }),

    details: build.query<CourseStep, DetailsRequest>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: CourseStepResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: generatePath(URL.COURSE_STEP_DETAILS, { id }),
      }),
    }),

    list: build.query<PaginationResponse<CourseStep>, ListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: ListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.COURSE_STEPS_LIST, params),
      }),
    }),

    update: build.mutation<CourseStep, UpdateRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: CourseStepResponse) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.COURSE_STEP_DETAILS, { id }),
      }),
    }),

    fieldSources: build.query<FormFieldSource[], FieldSourcesRequest>({
      transformResponse: (response: FormFieldSourcesResponse) => response.data,
      providesTags: (_result, _error, { courseId }) => [
        fieldSourcesListTag,
        getFieldSourcesByCourseTag(courseId),
      ],
      query: ({ courseId, ...params }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.COURSE_STEP_FIELD_SOURCES, { courseId }),
          params
        ),
      }),
    }),

    duplicate: build.mutation<CourseStep, DuplicateRequest>({
      transformResponse: (response: CourseStepResponse) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.COURSE_STEP_DUPLICATE, { id }),
      }),
      onQueryStarted: async (
        { id, courseId },
        { dispatch, getState, queryFulfilled }
      ) => {
        await queryFulfilled;
        dispatch(
          courseStepsApi.util.invalidateTags([{ id: 'LIST', type: tagType }])
        );
        const courseDetails = coursesApi.endpoints.details.select({
          id: courseId,
        })(getState() as any);
        if (courseDetails.data?.stepIds.includes(id)) {
          dispatch(
            coursesApi.util.invalidateTags([
              { id: courseId, type: coursesTagType },
            ])
          );
        }
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDuplicateMutation,
  useFieldSourcesQuery,
} = courseStepsApi;

export type { ListRequest };

export {
  getCourseStepTag,
  tagType as courseStepsTagType,
  fieldSourcesListTag as courseStepsFieldSourcesListTag,
  getFieldSourcesByCourseTag as getCourseStepsFieldSourcesTag,
};
